import { Fragment, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../features/auth/contexts/AuthContext";
import LanguageSelector from "../../shared/components/layout/LanguageSelector";
import { QuotaCounter } from "../../features/subscription/components";
import VerificationWarningBanner from "../../shared/components/banners/VerificationWarningBanner";
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { ROUTES } from "../../constants/routes";

// Sub-items under "Datenbearbeitung" group
const DATENBEARBEITUNG_LINKS = [
  { path: ROUTES.CSV_FILTER, translationKey: 'navBar.csvFilter' },
  { path: ROUTES.STANDARDISIERUNG, translationKey: 'navBar.rohdatenLaden', emphasize: true },
  { path: ROUTES.ZEITRASTEREINSTELLUNG, translationKey: 'navBar.datenvorbereitung' },
  { path: ROUTES.ANOMALIEERKENNUNG, translationKey: 'navBar.datenbearbeitung' },
  { path: ROUTES.DATA_ADJUSTMENTS, translationKey: 'navBar.datenanpassung' },
  { path: ROUTES.DATENWOLKE, translationKey: 'navBar.datenwolke' },
];

const DATENBEARBEITUNG_PATHS = DATENBEARBEITUNG_LINKS.map(l => l.path) as string[];

function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDatenbearbeitungOpen, setIsDatenbearbeitungOpen] = useState(true);
  const { t } = useTranslation('navigation');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getNavLinkClass = (path: string) => {
    const isActive = location.pathname === path;
    const baseClasses = "relative px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-all duration-200 block w-full text-left hover:bg-muted rounded-md";

    return `${baseClasses} ${
      isActive ? "bg-accent text-primary" : "text-foreground hover:text-foreground"
    }`;
  };

  const isAnyDatenbearbeitungActive = DATENBEARBEITUNG_PATHS.includes(location.pathname);

  return (
    <Fragment>
      {/* Overlay for when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar Toggle Button (always visible) */}
      <button
        className="fixed top-2 left-2 sm:top-4 sm:left-4 z-50 p-1.5 sm:p-2 rounded-lg bg-card shadow-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 sidebar-toggle-button"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        aria-expanded={isSidebarOpen}
        aria-label="Toggle navigation menu"
      >
        {isSidebarOpen ? (
          <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
        ) : (
          <FaBars className="w-5 h-5 sm:w-6 sm:h-6" />
        )}
      </button>

      {/* Sidebar */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-nav-title"
        aria-hidden={!isSidebarOpen}
        className={`fixed top-0 left-0 h-full bg-card shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } w-56 sm:w-64 p-3 sm:p-4 flex flex-col overflow-y-auto`}
      >
        <h2 id="mobile-nav-title" className="sr-only">
          {t('mobileNav.title')}
        </h2>
        {/* Logo */}
        <div className="flex-shrink-0 mb-4 sm:mb-6">
          <div className="mx-auto flex justify-center">
            <img src="/logo-header-light.svg" alt="Forecast Engine" className="h-12 w-auto" />
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-grow space-y-1 sm:space-y-2">
          {/* Datenbearbeitung group */}
          <div>
            <button
              onClick={() => setIsDatenbearbeitungOpen(!isDatenbearbeitungOpen)}
              className={`w-full flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 ${
                isAnyDatenbearbeitungActive
                  ? 'bg-accent text-primary'
                  : 'text-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <span>{t('navBar.datenbearbeitungGroup')}</span>
              <FaChevronDown className={`w-3 h-3 transition-transform duration-200 ${isDatenbearbeitungOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDatenbearbeitungOpen && (
              <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-border pl-2">
                {DATENBEARBEITUNG_LINKS.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`${getNavLinkClass(link.path)}${link.emphasize ? ' ring-2 ring-primary/60 font-semibold' : ''}`}
                    onClick={() => setIsSidebarOpen(false)}
                  >
                    {t(link.translationKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Training — same level as Datenbearbeitung */}
          <Link
            to={ROUTES.TRAINING}
            className={getNavLinkClass(ROUTES.TRAINING)}
            onClick={() => setIsSidebarOpen(false)}
          >
            {t('navBar.training')}
          </Link>

          {/* Forecast API */}
          <Link
            to={ROUTES.FORECAST_API}
            className={getNavLinkClass(ROUTES.FORECAST_API)}
            onClick={() => setIsSidebarOpen(false)}
          >
            Forecast API
          </Link>
        </nav>

        {/* Verification Warning Banner */}
        <div className="mt-auto">
          <VerificationWarningBanner />
        </div>

        {/* Quota Counter & Pricing */}
        <div className="space-y-2 sm:space-y-3">
          <QuotaCounter />

          {/* Pricing Link */}
          <Link
            to="/pricing"
            className={`block w-full px-2 sm:px-3 py-2 sm:py-2.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 border ${
              location.pathname === '/pricing'
                ? 'bg-accent border-primary/40 text-primary'
                : 'bg-muted border-border text-foreground hover:bg-accent hover:border-primary/30'
            }`}
            onClick={() => setIsSidebarOpen(false)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('navBar.pricing')}</span>
              </div>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>

        {/* User Actions (bottom of sidebar) */}
        <div className="space-y-2 sm:space-y-3 border-t pt-3 sm:pt-4 border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-medium text-foreground">{t('navBar.language')}</span>
            <LanguageSelector />
          </div>
          <button
            onClick={() => {
              handleLogout();
              setIsSidebarOpen(false);
            }}
            className="w-full bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-1.5 sm:space-x-2"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1" />
            </svg>
            <span>{t('navBar.logout')}</span>
          </button>
        </div>
      </div>
    </Fragment>
  );
}

export default NavBar;
